import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { channelSettingsApiClient } from '@/lib/api'
import type { Channel, SuppressionRule } from '@super/shared-types'
import { formatUSPhoneForDisplay } from '@super/shared-types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { ChannelSettingsForm } from '@/components/channel-settings/ChannelSettingsForm'
import { Skeleton } from '@/components/ui/skeleton'
import { useDocumentHead } from '@/hooks/use-document-head'
import {
  AlertCircle,
  Mail,
  Phone,
  MessageSquare,
  PhoneCall,
} from 'lucide-react'

interface ChannelFormState {
  active: boolean
  suppressionRules: SuppressionRule[]
  hasValidationErrors: boolean
}

interface CallChannelFormState {
  active: boolean
  suppressionRules: SuppressionRule[]
  hasValidationErrors: boolean
  suppressActionsOnHangup: boolean
}

interface ChannelOriginalState {
  active: boolean
  suppressionRules: SuppressionRule[]
}

interface CallChannelOriginalState {
  active: boolean
  suppressionRules: SuppressionRule[]
  suppressActionsOnHangup: boolean
}

type ChannelFormStates = {
  EMAIL: ChannelFormState
  SMS: ChannelFormState
  CALL: CallChannelFormState
}

type ChannelOriginalStates = {
  EMAIL: ChannelOriginalState
  SMS: ChannelOriginalState
  CALL: CallChannelOriginalState
}

export function ChannelSettingsPage() {
  useDocumentHead({
    title: 'Configure channel settings',
    description: 'Manage your channel settings',
  })

  const {
    currentCompany,
    emailDomain,
    isCompanyLoading,
    isAuthLoading,
    isInitialized,
    isSuperAdmin,
  } = useAppAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [formStates, setFormStates] = useState<ChannelFormStates>({
    EMAIL: { active: true, suppressionRules: [], hasValidationErrors: false },
    SMS: { active: true, suppressionRules: [], hasValidationErrors: false },
    CALL: {
      active: true,
      suppressionRules: [],
      hasValidationErrors: false,
      suppressActionsOnHangup: false,
    },
  })

  const [originalStates, setOriginalStates] = useState<ChannelOriginalStates>({
    EMAIL: { active: true, suppressionRules: [] },
    SMS: { active: true, suppressionRules: [] },
    CALL: {
      active: true,
      suppressionRules: [],
      suppressActionsOnHangup: false,
    },
  })

  useEffect(() => {
    if (currentCompany?.id) {
      loadChannelSettings()
    }
  }, [currentCompany?.id, isSuperAdmin])

  const loadChannelSettings = async () => {
    if (!currentCompany?.id) return

    try {
      setLoading(true)

      // Use super admin endpoints if user is super admin, otherwise use regular endpoints
      const [emailSettings, smsSettings, callSettings] = isSuperAdmin
        ? await Promise.all([
            channelSettingsApiClient.getChannelSettingsForCompany(
              currentCompany.id,
              'EMAIL',
            ),
            channelSettingsApiClient.getChannelSettingsForCompany(
              currentCompany.id,
              'SMS',
            ),
            channelSettingsApiClient.getChannelSettingsForCompany(
              currentCompany.id,
              'CALL',
            ),
          ])
        : await Promise.all([
            channelSettingsApiClient.getChannelSettings('EMAIL'),
            channelSettingsApiClient.getChannelSettings('SMS'),
            channelSettingsApiClient.getChannelSettings('CALL'),
          ])

      const emailState = {
        active: emailSettings?.active ?? true,
        suppressionRules: emailSettings?.suppressionRules || [],
      }
      const smsState = {
        active: smsSettings?.active ?? true,
        suppressionRules: smsSettings?.suppressionRules || [],
      }
      const callState = {
        active: callSettings?.active ?? true,
        suppressionRules: callSettings?.suppressionRules || [],
        suppressActionsOnHangup:
          (callSettings?.metadata?.suppressActionsOnHangup as
            | boolean
            | undefined) ?? false,
      }

      setOriginalStates({
        EMAIL: emailState,
        SMS: smsState,
        CALL: callState,
      })

      setFormStates({
        EMAIL: { ...emailState, hasValidationErrors: false },
        SMS: { ...smsState, hasValidationErrors: false },
        CALL: { ...callState, hasValidationErrors: false },
      })
    } catch (error) {
      console.error('Failed to load channel settings:', error)
      toast.error('Failed to load channel settings')
    } finally {
      setLoading(false)
    }
  }

  const updateChannelState = (
    channel: Channel,
    updates: Partial<CallChannelFormState>,
  ) => {
    setFormStates(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        ...updates,
      },
    }))
  }

  // Deep equality check for suppression rules
  const areSuppressionRulesEqual = (
    rules1: SuppressionRule[],
    rules2: SuppressionRule[],
  ): boolean => {
    if (rules1.length !== rules2.length) return false
    return JSON.stringify(rules1) === JSON.stringify(rules2)
  }

  // Check if a channel has actual changes compared to original state
  const channelHasChanges = (channel: Channel): boolean => {
    const current = formStates[channel]
    const original = originalStates[channel]

    if (current.active !== original.active) return true
    if (
      channel === 'CALL' &&
      formStates.CALL.suppressActionsOnHangup !==
        originalStates.CALL.suppressActionsOnHangup
    )
      return true
    if (
      !areSuppressionRulesEqual(
        current.suppressionRules,
        original.suppressionRules,
      )
    ) {
      return true
    }

    return false
  }

  const hasAnyChanges = () => {
    const channels: Channel[] = ['EMAIL', 'SMS', 'CALL']
    return channels.some(channel => channelHasChanges(channel))
  }

  const getChangedCount = () => {
    let count = 0
    const channels: Channel[] = ['EMAIL', 'SMS', 'CALL']

    channels.forEach(channel => {
      if (channelHasChanges(channel)) {
        const current = formStates[channel]
        const original = originalStates[channel]

        // Count active toggle change
        if (current.active !== original.active) {
          count += 1
        }

        // Count suppressActionsOnHangup toggle change
        if (
          channel === 'CALL' &&
          formStates.CALL.suppressActionsOnHangup !==
            originalStates.CALL.suppressActionsOnHangup
        ) {
          count += 1
        }

        // Count suppression rule changes
        if (
          !areSuppressionRulesEqual(
            current.suppressionRules,
            original.suppressionRules,
          )
        ) {
          count += Math.max(current.suppressionRules.length, 1)
        }
      }
    })

    return count
  }

  const validateChannel = (channel: Channel): boolean => {
    const state = formStates[channel]

    // Check if all suppression rules are complete
    for (const rule of state.suppressionRules) {
      if (
        !rule.field ||
        !rule.operator ||
        rule.value === '' ||
        rule.value === null ||
        rule.value === undefined
      ) {
        return false
      }
      // For array values (multi-select), check if array has items
      if (Array.isArray(rule.value) && rule.value.length === 0) {
        return false
      }
    }

    return true
  }

  const validateAll = (): boolean => {
    const channels: Channel[] = ['EMAIL', 'SMS', 'CALL']
    const invalidChannels: string[] = []
    const channelsWithValidationErrors: string[] = []

    for (const channel of channels) {
      if (channelHasChanges(channel)) {
        if (!validateChannel(channel)) {
          invalidChannels.push(channel)
        }
        if (formStates[channel].hasValidationErrors) {
          channelsWithValidationErrors.push(channel)
        }
      }
    }

    if (channelsWithValidationErrors.length > 0) {
      toast.error(
        `Please fix validation errors for: ${channelsWithValidationErrors.join(', ')}`,
      )
      return false
    }

    if (invalidChannels.length > 0) {
      toast.error(
        `Please complete all suppression rules for: ${invalidChannels.join(', ')}`,
      )
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!validateAll()) {
      return
    }
    if (!currentCompany?.id) return false

    setSaving(true)
    try {
      const updates: Array<Promise<any>> = []
      const channels: Channel[] = ['EMAIL', 'SMS', 'CALL']

      // Save each channel that has changes
      channels.forEach(channel => {
        if (channelHasChanges(channel)) {
          const state = formStates[channel]
          const updateDto = {
            active: state.active,
            suppressionRules:
              state.suppressionRules.length > 0 ? state.suppressionRules : null,
            ...(channel === 'CALL' &&
              formStates.CALL.suppressActionsOnHangup !==
                originalStates.CALL.suppressActionsOnHangup && {
                metadata: {
                  suppressActionsOnHangup:
                    formStates.CALL.suppressActionsOnHangup,
                },
              }),
          }

          // Use super admin endpoint if user is super admin
          const updatePromise = isSuperAdmin
            ? channelSettingsApiClient.updateChannelSettingsForCompany(
                currentCompany.id,
                channel,
                updateDto,
              )
            : channelSettingsApiClient.updateChannelSettings(channel, updateDto)

          updates.push(updatePromise)
        }
      })

      await Promise.all(updates)

      // Update original states to match current states
      const newOriginalStates: ChannelOriginalStates = {
        EMAIL: {
          active: formStates.EMAIL.active,
          suppressionRules: formStates.EMAIL.suppressionRules,
        },
        SMS: {
          active: formStates.SMS.active,
          suppressionRules: formStates.SMS.suppressionRules,
        },
        CALL: {
          active: formStates.CALL.active,
          suppressionRules: formStates.CALL.suppressionRules,
          suppressActionsOnHangup: formStates.CALL.suppressActionsOnHangup,
        },
      }
      setOriginalStates(newOriginalStates)

      toast.success('Channel settings updated successfully')
      return true
    } catch (error) {
      console.error('Failed to save channel settings:', error)
      toast.error('Failed to save channel settings')
      return false
    } finally {
      setSaving(false)
    }
  }

  // Show nothing while initial auth is loading
  if (isAuthLoading || !isInitialized) {
    return null
  }

  // Show skeleton during loading
  if (loading || isCompanyLoading || !currentCompany) {
    return (
      <div className="h-full pt-16">
        <div className="space-y-6 p-6 pt-0 mt-6">
          <PageHeader title="Configure channel settings" />
          <div className="space-y-6">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-4 w-64 mt-2" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-32 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Contact points shown as the gray subtitle under each card header. Phone
  // comes from the company record; the email address is derived from the
  // company slug + the inbound email domain the API resolves from EMAIL_DOMAIN.
  const phoneDisplay = currentCompany.phone
    ? formatUSPhoneForDisplay(currentCompany.phone)
    : null
  const emailDisplay =
    currentCompany.slug && emailDomain
      ? `${currentCompany.slug}@${emailDomain}`
      : null

  return (
    <div className="h-full pt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0 pb-24 mt-6">
          <PageHeader title="Configure channel settings" />

          {/* Email Channel Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Email</CardTitle>
                  {emailDisplay && (
                    <span className="text-sm font-normal text-muted-foreground">
                      {emailDisplay}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="email-active"
                    checked={formStates.EMAIL.active}
                    onCheckedChange={checked =>
                      updateChannelState('EMAIL', { active: checked })
                    }
                  />
                  <Label htmlFor="email-active">
                    {formStates.EMAIL.active ? 'Active' : 'Inactive'}
                  </Label>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {formStates.EMAIL.active ? (
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Suppression Rules
                  </Label>
                  <ChannelSettingsForm
                    channel="EMAIL"
                    rules={formStates.EMAIL.suppressionRules}
                    onChange={rules =>
                      updateChannelState('EMAIL', { suppressionRules: rules })
                    }
                    onValidationChange={hasErrors =>
                      updateChannelState('EMAIL', {
                        hasValidationErrors: hasErrors,
                      })
                    }
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-600 animate-in fade-in duration-300">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-normal">
                    You will not receive communications from Email
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phone Channel Settings (SMS + Voice share the company number) */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Phone</CardTitle>
                {phoneDisplay && (
                  <span className="text-sm font-normal text-muted-foreground">
                    {phoneDisplay}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SMS subsection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold">SMS</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="sms-active"
                      checked={formStates.SMS.active}
                      onCheckedChange={checked =>
                        updateChannelState('SMS', { active: checked })
                      }
                    />
                    <Label htmlFor="sms-active">
                      {formStates.SMS.active ? 'Active' : 'Inactive'}
                    </Label>
                  </div>
                </div>
                {formStates.SMS.active ? (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Suppression Rules
                    </Label>
                    <ChannelSettingsForm
                      channel="SMS"
                      rules={formStates.SMS.suppressionRules}
                      onChange={rules =>
                        updateChannelState('SMS', { suppressionRules: rules })
                      }
                      onValidationChange={hasErrors =>
                        updateChannelState('SMS', {
                          hasValidationErrors: hasErrors,
                        })
                      }
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-600 animate-in fade-in duration-300">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm font-normal">
                      You will not receive communications from SMS
                    </span>
                  </div>
                )}
              </div>

              <div className="border-t" />

              {/* Voice/Call subsection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PhoneCall className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-semibold">Voice</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="call-active"
                      checked={formStates.CALL.active}
                      onCheckedChange={checked =>
                        updateChannelState('CALL', { active: checked })
                      }
                    />
                    <Label htmlFor="call-active">
                      {formStates.CALL.active ? 'Active' : 'Inactive'}
                    </Label>
                  </div>
                </div>
                {formStates.CALL.active ? (
                  <>
                    <div className="flex items-start space-x-2">
                      <Switch
                        id="call-suppress-hangup"
                        checked={formStates.CALL.suppressActionsOnHangup}
                        onCheckedChange={checked =>
                          updateChannelState('CALL', {
                            suppressActionsOnHangup: checked,
                          })
                        }
                      />
                      <div className="grid gap-0.5">
                        <Label htmlFor="call-suppress-hangup">
                          Do not perform post-call actions on hangups
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          When enabled, no SMS or workflow notifications are
                          sent when a caller hangs up without speaking.
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Suppression Rules
                      </Label>
                      <ChannelSettingsForm
                        channel="CALL"
                        rules={formStates.CALL.suppressionRules}
                        onChange={rules =>
                          updateChannelState('CALL', {
                            suppressionRules: rules,
                          })
                        }
                        onValidationChange={hasErrors =>
                          updateChannelState('CALL', {
                            hasValidationErrors: hasErrors,
                          })
                        }
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-amber-600 animate-in fade-in duration-300">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-sm font-normal">
                      You will not receive communications from Voice
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <FloatingActionIslandContent
          show={hasAnyChanges()}
          componentId="channel-settings-save"
          priority={20}
        >
          <div className="inline-flex w-full max-w-2xl">
            <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
              <span className="font-medium">
                <span className="mr-1">
                  {getChangedCount()} <span>unsaved&nbsp;</span>
                  {getChangedCount() === 1 ? 'change' : 'changes'}
                </span>
              </span>
            </div>

            <div className="flex items-center space-x-2 justify-end pointer-events-auto">
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                variant="form"
              >
                {saving ? 'Saving...' : 'Save changes'}
              </Button>
            </div>
          </div>
        </FloatingActionIslandContent>

        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
